import { defineComponent as _defineComponent } from 'vue';
import { line, curveBasis } from "d3";
import { computed } from "vue";
import { getConnectionId } from "@/stores/workflowConnectionStore";
import { useWorkflowStateStore } from "@/stores/workflowEditorStateStore";
import { useConnectionStore } from "@/stores/workflowConnectionStore";
import { useWorkflowStepStore } from "@/stores/workflowStepStore";
export default /*#__PURE__*/ _defineComponent({
    __name: 'SVGConnection',
    props: {
        id: String,
        connection: {
            type: Object,
            required: true,
        },
        terminalPosition: {
            type: Object,
            default: null,
        },
    },
    setup(__props) {
        const props = __props;
        const ribbonMargin = 4;
        const curve = line().curve(curveBasis);
        const stateStore = useWorkflowStateStore();
        const connectionStore = useConnectionStore();
        const stepStore = useWorkflowStepStore();
        const outputPos = computed(() => {
            if (props.terminalPosition) {
                return {
                    startX: props.terminalPosition.startX,
                    startY: props.terminalPosition.startY,
                };
            }
            else {
                const pos = stateStore.getOutputTerminalPosition(props.connection.output.stepId, props.connection.output.name);
                if (pos) {
                    return pos;
                }
                else {
                    return null;
                }
            }
        });
        const inputPos = computed(() => {
            if (props.terminalPosition) {
                return {
                    endX: props.terminalPosition.endX,
                    endY: props.terminalPosition.endY,
                };
            }
            else {
                const pos = stateStore.getInputTerminalPosition(props.connection.input.stepId, props.connection.input.name);
                if (pos) {
                    return pos;
                }
                else {
                    return null;
                }
            }
        });
        const connectionPosition = computed(() => {
            if (inputPos.value && outputPos.value) {
                return {
                    startX: outputPos.value.startX,
                    startY: outputPos.value.startY,
                    endX: inputPos.value.endX,
                    endY: inputPos.value.endY,
                };
            }
            return null;
        });
        const outputIsMappedOver = computed(() => { var _a; return (_a = stepStore.stepMapOver[props.connection.output.stepId]) === null || _a === void 0 ? void 0 : _a.isCollection; });
        const inputIsMappedOver = computed(() => { var _a, _b; return (_b = (_a = stepStore.stepInputMapOver[props.connection.input.stepId]) === null || _a === void 0 ? void 0 : _a[props.connection.input.name]) === null || _b === void 0 ? void 0 : _b.isCollection; });
        const outputIsOptional = computed(() => {
            var _a, _b;
            return Boolean(((_a = stepStore.getStep(props.connection.output.stepId)) === null || _a === void 0 ? void 0 : _a.when) ||
                ((_b = stepStore
                    .getStep(props.connection.output.stepId)) === null || _b === void 0 ? void 0 : _b.outputs.find((output) => output.name === props.connection.output.name && output.optional)));
        });
        const connectionIsValid = computed(() => {
            return !connectionStore.invalidConnections[getConnectionId(props.connection)];
        });
        const lineOffsets = computed(() => getLineOffsets(inputIsMappedOver.value, outputIsMappedOver.value));
        // minimum line shift
        const baseLineShift = 15;
        // how much the x distance influences line shift
        const lineShiftGrowFactorX = 0.15;
        // how much the y distance influences line shift
        const lineShiftGrowFactorY = 0.08;
        const lineShiftX = computed(() => {
            const position = connectionPosition.value;
            if (!position) {
                return baseLineShift;
            }
            const distanceX = Math.abs(position.endX - position.startX - baseLineShift);
            const distanceY = Math.abs(position.endY - position.startY);
            const forward = position.endX >= position.startX;
            if (forward) {
                const growX = distanceX * lineShiftGrowFactorX;
                const growY = distanceY * lineShiftGrowFactorY;
                return baseLineShift + growX + growY;
            }
            else {
                // reverse variant had reduced growth, but a higher base
                const growX = distanceX * lineShiftGrowFactorX * 0.5;
                const growY = distanceY * lineShiftGrowFactorY * 0.5;
                return baseLineShift * 2 + growX + growY;
            }
        });
        const paths = computed(() => {
            const position = connectionPosition.value;
            const offsets = lineOffsets.value;
            if (!position || !offsets) {
                return [];
            }
            const forward = position.endX >= position.startX;
            const lines = [...Array(offsets.numOffsets).keys()].map((offsetIndex) => {
                const startOffset = offsets.startOffsets[offsetIndex] || 0;
                const endOffset = offsets.endOffsets[offsetIndex] || 0;
                if (forward) {
                    return [
                        [position.startX, position.startY + startOffset],
                        [position.startX + lineShiftX.value, position.startY + startOffset],
                        [position.endX - lineShiftX.value, position.endY + endOffset],
                        [position.endX, position.endY + endOffset],
                    ];
                }
                else {
                    // reverse variant has two additional control points to smooth curve
                    const lineShiftY = (position.endY - position.startY) / 2;
                    return [
                        [position.startX, position.startY + startOffset],
                        [position.startX + lineShiftX.value, position.startY + startOffset],
                        [position.startX + lineShiftX.value, position.startY + lineShiftY + startOffset],
                        [position.endX - lineShiftX.value, position.endY - lineShiftY + endOffset],
                        [position.endX - lineShiftX.value, position.endY + endOffset],
                        [position.endX, position.endY + endOffset],
                    ];
                }
            });
            return lines.map((l) => curve(l));
        });
        const lineWidth = computed(() => {
            if (inputIsMappedOver.value || outputIsMappedOver.value) {
                return 2;
            }
            else {
                return 4;
            }
        });
        const connectionClass = computed(() => {
            const classList = ["connection"];
            if (outputIsOptional.value) {
                classList.push("optional");
            }
            if (!connectionIsValid.value) {
                classList.push("invalid");
            }
            return classList.join(" ");
        });
        function generateLineOffsets(inputIsMappedOver, outputIsMappedOver) {
            const offsets = [-2 * ribbonMargin, -ribbonMargin, 0, ribbonMargin, 2 * ribbonMargin];
            let startOffsets = [0];
            let endOffsets = [0];
            let numOffsets = 1;
            if (outputIsMappedOver) {
                startOffsets = offsets;
                numOffsets = offsets.length;
            }
            if (inputIsMappedOver) {
                endOffsets = offsets;
                numOffsets = offsets.length;
            }
            return { numOffsets, startOffsets, endOffsets };
        }
        const lineOffsetDictionary = {
            "false-false": generateLineOffsets(false, false),
            "true-false": generateLineOffsets(true, false),
            "false-true": generateLineOffsets(false, true),
            "true-true": generateLineOffsets(true, true),
        };
        function getLineOffsets(inputIsMappedOver, outputIsMappedOver) {
            return lineOffsetDictionary[`${inputIsMappedOver !== null && inputIsMappedOver !== void 0 ? inputIsMappedOver : false}-${outputIsMappedOver !== null && outputIsMappedOver !== void 0 ? outputIsMappedOver : false}`];
        }
        function keyForIndex(index) {
            var _a;
            return `${(_a = props.id) !== null && _a !== void 0 ? _a : "no-key"}-${index}`;
        }
        return { __sfc: true, props, ribbonMargin, curve, stateStore, connectionStore, stepStore, outputPos, inputPos, connectionPosition, outputIsMappedOver, inputIsMappedOver, outputIsOptional, connectionIsValid, lineOffsets, baseLineShift, lineShiftGrowFactorX, lineShiftGrowFactorY, lineShiftX, paths, lineWidth, connectionClass, generateLineOffsets, lineOffsetDictionary, getLineOffsets, keyForIndex };
    }
});
